'use client';
import { Button, Separator } from '@baseball-near-you/ui';
import { login } from '@bb/app/login/actions';
import { PATHS } from '@bb/lib/constants';
import { QueryKey } from '@bb/lib/hooks/QueryKey';
import { tryGetCurrentClientUser } from '@bb/lib/session/client';
import { useQueryClient } from '@tanstack/react-query';
import type { Route } from 'next';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import posthog from 'posthog-js';
import { Suspense } from 'react';
import { useFormStatus } from 'react-dom';
import { LoginWithGoogleButton } from './LoginWithGoogleButton';
import { Messages } from './Messages';

export function LoginForm(props: {
  onSignupClick: () => void;
  onLoginSuccess?: () => void;
}) {
  const searchParams = useSearchParams();
  const message = searchParams.get('message');
  const redirectPath = searchParams.get('redirect');
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { push } = useRouter();

  return message ? (
    <Suspense>
      <Messages />
    </Suspense>
  ) : (
    <form
      className="flex h-min w-full flex-1 flex-col gap-2 text-foreground"
      action={async (data) => {
        await login(data, pathname);
        const user = await tryGetCurrentClientUser();
        if (user) {
          posthog.identify(user.id, {
            user_id: user.id,
            user_createdAt: user.createdAt,
            user_firstName: user.firstName,
            user_lastName: user.lastName,
            user_email: user.email,
            user_phone: user.phoneNumber,
          });
          queryClient.setQueryData([QueryKey.User], user);
          props.onLoginSuccess?.();
          if (redirectPath) {
            push(decodeURIComponent(redirectPath) as Route);
          } else if (
            pathname.includes(PATHS.Onboarding) ||
            pathname.includes(PATHS.Login) ||
            pathname.includes(PATHS.SignUp)
          ) {
            push(PATHS.Teams);
          }
          // window.location.reload();
        } else {
          console.log(
            'Login Error - the user exists on supabase authentication but not within our own database.'
          );
          push(
            '/login?error=There was an issue logging in. Please try again or contact us at dev@baseballnearyou.com for more help.'
          );
        }
      }}
    >
      <p className="title text-xl">LOGIN</p>
      <Suspense>
        <Messages />
      </Suspense>
      <label className="text-md" htmlFor="email">
        Email
      </label>
      <input
        className="mb-6 rounded-md border bg-inherit px-4 py-2"
        name="email"
        placeholder="you@example.com"
        required
      />
      <label className="text-md" htmlFor="password">
        Password
      </label>
      <input
        className="mb-6 rounded-md border bg-inherit px-4 py-2"
        type="password"
        name="password"
        placeholder="••••••••"
        required
      />
      <LoginButton />
      <Separator className="mb-2" />
      <LoginWithGoogleButton />
      <p className="text-center text-gray-400 text-xs">
        Don't have an account?
      </p>
      <Link
        href={`${PATHS.Onboarding}?redirect=${encodeURIComponent(pathname)}`}
      >
        <Button
          type="button"
          // onClick={props.onSignupClick}
          className="w-full rounded border border-gray-700 bg-white py-2 text-black"
          variant="secondary"
        >
          Sign Up
        </Button>
      </Link>
    </form>
  );
}

const LoginButton = () => {
  const { pending } = useFormStatus();
  return (
    <Button
      className="mb-2 rounded bg-red py-2 text-white hover:bg-red/90"
      loading={pending}
    >
      Sign In
    </Button>
  );
};
